import React from 'react';
import { Check, Mail, Sparkles, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  formatFeatureName,
  getTierByName,
  getUpgradeTier,
  hasFeature,
} from '@/lib/tiers';

function buildComparisonFeatures(feature, currentTier, recommendedTier) {
  return Array.from(
    new Set([feature, ...currentTier.features, ...recommendedTier.features])
  ).slice(0, 7);
}

function PlanCard({ tier, features, emphasized = false }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${
        emphasized
          ? 'border-blue-500/60 bg-gradient-to-br from-blue-500/20 via-slate-900 to-slate-950 shadow-[0_0_40px_rgba(59,130,246,0.15)]'
          : 'border-slate-700 bg-slate-900/80'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold text-white">{tier.name}</p>
          <p className="mt-1 text-sm text-slate-400">{tier.price}</p>
        </div>
        {emphasized ? (
          <Badge className="border-blue-400/50 bg-blue-500/15 text-blue-100">Recommended</Badge>
        ) : null}
      </div>

      <div className="mt-5 space-y-3">
        {features.map((featureKey) => {
          const included = hasFeature(tier.id, featureKey);

          return (
            <div key={`${tier.id}-${featureKey}`} className="flex items-center gap-3">
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full ${
                  included ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-500'
                }`}
              >
                {included ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
              </span>
              <span className={included ? 'text-slate-200' : 'text-slate-500'}>
                {formatFeatureName(featureKey)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function UpgradePrompt({
  feature,
  currentTier,
  open = false,
  onOpenChange = () => {},
}) {
  const currentPlan = getTierByName(currentTier);
  const recommendedPlan = getUpgradeTier(currentTier, feature);
  const comparisonFeatures = buildComparisonFeatures(feature, currentPlan, recommendedPlan);
  const salesSubject = encodeURIComponent(
    `Unlock ${formatFeatureName(feature)} for ${recommendedPlan.name}`
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl border-slate-700 bg-[#07111f] p-0 text-slate-200">
        <div className="overflow-hidden rounded-lg">
          <div className="border-b border-slate-800 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.28),_transparent_45%),linear-gradient(180deg,_rgba(15,23,42,0.98),_rgba(2,6,23,1))] px-6 py-6">
            <DialogHeader className="space-y-3 text-left">
              <Badge className="w-fit border-amber-400/40 bg-amber-500/10 text-amber-200">
                <Sparkles className="mr-1 h-3.5 w-3.5" />
                Premium capability
              </Badge>
              <DialogTitle className="text-2xl font-semibold text-white">
                Unlock {formatFeatureName(feature)}
              </DialogTitle>
              <p className="max-w-2xl text-sm text-slate-400">
                Move from {currentPlan.name} to {recommendedPlan.name} to activate this workflow and
                expand your monitoring coverage.
              </p>
            </DialogHeader>
          </div>

          <div className="space-y-6 px-6 py-6">
            <div className="grid gap-4 lg:grid-cols-2">
              <PlanCard tier={currentPlan} features={comparisonFeatures} />
              <PlanCard tier={recommendedPlan} features={comparisonFeatures} emphasized />
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-800 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium text-white">Ready for a plan change?</p>
                <p className="text-sm text-slate-500">
                  Sales can help configure rollout, deployment, and policy migration.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Button asChild className="bg-blue-600 text-white hover:bg-blue-500">
                  <a href={`mailto:sales@amrsecureops.com?subject=${salesSubject}`}>
                    <Mail className="h-4 w-4" />
                    Contact Sales
                  </a>
                </Button>
                <Button asChild variant="link" className="px-0 text-slate-300 hover:text-white">
                  <a
                    href="https://amrsecureops.com/pricing"
                    rel="noreferrer"
                    target="_blank"
                  >
                    View All Plans
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
